import get from 'lodash/get';
import { select, take, put, call } from 'redux-saga/effects';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import { renameEvent, editEventHTML } from '../../actions/events';
import { findQuest } from '../../reducers/quests';
import { isFetchQuestEnding } from '../quests';
import { API_METHODS } from './routeConstants';

/**
 * Triggers sending to backend newly added route point via `ADD_ROUTE_POINT` action
 * @param {Object} action
 * @param {Number} action.questId - id of the quest point relate
 * @param {RoutePoint} action.routePoint - added route point to sync
 * @param {Boolean} action.isSync - should quest be fetched after route point synced
 * @param {Boolean} action.isSend - should route point be sent to the backend
 */
export function* syncAddedRoutePoint({
  questId,
  routePoint,
  isSync,
  isSend = true,
}) {
  if (isSend) {
    yield put({
      type: types.SEND_ROUTE_POINT,
      questId,
      routePoint,
      purpose: 'add',
      isSync,
    });
  }
}

/**
 * Edits route point and bound to it event
 * @param {Object} action
 * @param {String} action.questId - id of the editing quest
 * @param {Object} action.routePoint - fields of the route point to change
 * @param {Object} action.event - data to be changed in the event description
 * @param {String?} action.event.description - will be added as event `html` content
 * @param {Boolean} action.isSync - should quest be refetched after all
 */
export function* syncEditedRoutePoint({
  questId,
  routePoint,
  event = {},
  isSync = true,
}) {
  const eventId = routePoint.guid;
  const boundEvent = findQuest(
    yield select((state) => state.quests.quests),
    questId
  ).events.find((e) => e.id === eventId);
  yield put({
    type: types.SEND_ROUTE_POINT,
    questId,
    routePoint,
    purpose: 'edit',
    isSync,
  });

  if (boundEvent) {
    const html = get(
      boundEvent.content.find((c) => c.contentType === 'html'),
      'data',
      ''
    ).replace(/<(.|\n)*?>/g, '');

    if (boundEvent.title !== routePoint.name && routePoint.name)
      yield put(renameEvent({ title: routePoint.name, questId, eventId }));
    if (typeof event.description === 'string' && !html)
      yield put(
        editEventHTML({
          html: event.description,
          visible: true,
          questId,
          eventId,
        })
      );
  }
}

/**
 * Triggers sending to backend recently removed route point via `REMOVE_ROUTE_POINT` action
 * @param {Object} action
 * @param {Number} action.questId - id of the quest point relate
 * @param {RoutePoint} action.routePoint - added route point to sync
 * @param {Boolean} action.isSync - should quest be fetched after route point synced
 * @param {Boolean} action.isSend - should route point be deleted on the backend
 */
export function* syncRemovedRoutePoint({
  questId,
  routePoint,
  isSync,
  isSend = true,
}) {
  if (isSend) {
    yield put({
      type: types.SEND_ROUTE_POINT,
      questId,
      routePoint,
      purpose: 'remove',
      isSync,
    });
  }
}

/**
 * Sends new/edited route point to the server
 * @param {Object} $
 * @param {String} $.purpose - which type of API to use (`"add"`, `"edit"` or `"remove"`)
 * @param {Number} $.questId - id of the quest to sync route points
 * @param {RoutePoint} $.routePoint - point to add/edit
 * @param {Boolean} $.isSync - should quest be refetched after all
 */
export function* sendRoutePoint({
  questId,
  routePoint,
  purpose = 'add',
  isSync = true,
}) {
  const sendData = { questId, routePoint, purpose };

  const method = API_METHODS[purpose];
  try {
    yield call(Api[method], `v2/quests/${questId}/point/`, {
      data: routePoint,
    });
  } catch (error) {
    yield put({ type: types.SEND_ROUTE_POINT_FAILURE, ...sendData });
    yield put({ type: types.NETWORK_ERROR, error });
    yield put({ type: types.FETCH_QUEST, id: questId });
    return;
  }

  if (isSync) {
    yield put({ type: types.FETCH_QUEST, id: questId });

    /**
     * Checking if quest with updated route points was correctly fetched
     */
    const dispatchedAction = yield take(isFetchQuestEnding);
    if (dispatchedAction.type !== types.RECEIVE_QUEST) {
      return;
    }
  }
  yield put({ type: types.SEND_ROUTE_POINT_SUCCESS, ...sendData });
}
